import {isAxiosError} from 'axios';
import clsx from 'clsx';
import React, {useEffect, useMemo, useState} from 'react';
import {Box, Stack, TextField} from '@mui/material';
import {Body, Button, Icon} from '@lightricks/react-design-system';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {HTTP_STATUS} from '@/config/constants';
import creatorsFetchers from '@/api/fetchers/creators';
import {isOffPlatformCreator} from '@/views/creators/creator-crm/utils/offPlatformSource';
import AutocompleteInput, {Option} from '@/components/autocomplete-input';
import useRaiseCreatorInvitedToCampaignSuccess from '@/hooks/flash-messages/use-raise-creator-invited-to-campaign-success/useRaiseCreatorInvitedToCampaignSuccess';
import {
  useInviteCreatorsToCampaignAll,
  useSendCampaignInvites,
} from '@/hooks/mutations/creators/use-invite-creators-to-campaign';
import useAgencyQuery from '@/hooks/queries/use-agency-query';
import useBrandCampaignsQuery from '@/hooks/queries/use-brand-campaigns-query';
import {CampaignListItem} from '@/hooks/queries/use-brand-campaigns-query/useBrandCampaignsQuery';
import useBrandId from '@/hooks/use-brand-id';
import {ActionProps} from './ActionProps';
import styles from './AddToCampaign.module.scss';
import ContentContainer from './ContentContainer';
import SubmitButtonContainer from './SubmitButtonContainer';
import campaignInvitesSummary, {
  hasSentCampaignInvites,
} from './campaignInvitesSummary';

const TRANSLATION_PREFIX =
  'views.creators.components.creator-action.add-to-campaign';

const PERSONAL_NOTE_MAX_LENGTH = 500;
// Must match Crm::OffPlatform::CampaignInviteService::URL_PATTERN in PPSuite.
const PERSONAL_NOTE_LINK_PATTERN = /\b(?:https?:\/\/|www\.)\S+/i;

const renderOption = (
  optionProps: React.HTMLAttributes<HTMLLIElement>,
  option: Option
) => {
  if (option.value.isInvited) {
    return (
      <li
        {...optionProps}
        className={clsx(
          optionProps.className,
          styles.invitedLabel,
          option.disabled ? styles.disabled : ''
        )}
      >
        <div>{option.label}</div>
        <div className={clsx(styles.invitedIconContainer, styles.disabled)}>
          <Icon size="small" appearance="disabled" name="Actions-Accept" />
          <Body size="sm">{translate('invited')}</Body>
        </div>
      </li>
    );
  }
  return (
    <li
      {...optionProps}
      className={`${optionProps.className} ${
        option.disabled ? styles.disabled : ''
      }`}
    >
      <span>{option.label}</span>
    </li>
  );
};

function AddToCampaign(
  props: ActionProps & {setHasActiveCampaigns: (hasActive: boolean) => void}
) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNoActiveCampaignsModalOpen, setIsNoActiveCampaignsModalOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const {
    creators,
    selectedCampaignId,
    onSubmitSuccess,
    setHasActiveCampaigns,
    isAllFilterMatchingSelected,
    searchSessionId,
  } = props;
  const brandId = useBrandId();
  const {data: agency} = useAgencyQuery({brandId});
  const sendCampaignInvites = useSendCampaignInvites();
  const inviteCreatorsToCampaignAll = useInviteCreatorsToCampaignAll();
  const raiseCreatorInvitedToCampaignSuccess =
    useRaiseCreatorInvitedToCampaignSuccess();
  const [personalNote, setPersonalNote] = useState('');
  const offPlatformCount = creators.filter(isOffPlatformCreator).length;
  const showPersonalNote = isAllFilterMatchingSelected || offPlatformCount > 0;

  const {
    brandCampaigns: campaignsAcceptingProposals,
    isLoading: isCampaignsLoading,
  } = useBrandCampaignsQuery({
    brandId,
    states: ['accepting_proposals'],
  });

  const [fullyInvitedCampaignIds, setFullyInvitedCampaignIds] = useState<
    Set<string>
  >(new Set());

  useEffect(() => {
    const fetchInvitationStatuses = async () => {
      try {
        setFullyInvitedCampaignIds(new Set());
        const creatorIds = creators.map((creator) => creator.id);
        const invitedCampaignIds = await Promise.all(
          (campaignsAcceptingProposals || []).map(async (campaign) => {
            const campaignStatuses =
              await creatorsFetchers.checkInvitationStatusBatch({
                campaignId: campaign.id,
              });
            const invitedIds = new Set(
              campaignStatuses
                .filter((status) => status.invited)
                .map((status) => status.id)
            );
            return creatorIds.every((id) => invitedIds.has(id))
              ? campaign.id
              : null;
          })
        );

        setFullyInvitedCampaignIds(
          new Set(invitedCampaignIds.filter((id): id is string => !!id))
        );
      } catch (error) {
        setFullyInvitedCampaignIds(new Set());
      }
    };

    if (
      campaignsAcceptingProposals?.length &&
      creators.length &&
      !isAllFilterMatchingSelected
    ) {
      fetchInvitationStatuses();
    } else {
      setFullyInvitedCampaignIds(new Set());
    }
  }, [campaignsAcceptingProposals, creators, isAllFilterMatchingSelected]);

  useEffect(() => {
    if (!isCampaignsLoading) {
      setIsLoading(false);
    }
  }, [isCampaignsLoading]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (isAllFilterMatchingSelected && searchSessionId) {
        await inviteCreatorsToCampaignAll.mutateAsync({
          agencyId: agency?.id ?? '',
          searchSessionId,
          campaignId: selectedOption?.id ?? '',
          personalNote: personalNote.trim() || undefined,
        });
        onSubmitSuccess(selectedOption?.id);
      } else {
        const result = await sendCampaignInvites.mutateAsync({
          agencyId: agency?.id ?? '',
          campaignId: selectedOption?.id ?? '',
          creators,
          personalNote: personalNote.trim() || undefined,
        });
        onSubmitSuccess(selectedOption?.id);
        if (offPlatformCount === 0) {
          raiseCreatorInvitedToCampaignSuccess(creators.length);
        } else {
          const hasSent = hasSentCampaignInvites(result);
          raiseFlashMessage({
            status: hasSent ? 'success' : 'warning',
            message:
              campaignInvitesSummary(result) ||
              translate('No invitations were sent'),
          });
        }
      }
    } catch (error) {
      const serverMessage = isAxiosError<{error?: string}>(error)
        ? error.response?.status === HTTP_STATUS.UNPROCESSABLE_ENTITY &&
          error.response.data?.error
        : null;
      raiseFlashMessageError(
        serverMessage ? {title: serverMessage, subtitle: ''} : undefined
      );
    }
    setIsSubmitting(false);
  };

  const mappedOptions = useMemo(
    () =>
      (campaignsAcceptingProposals || []).map((campaign: CampaignListItem) => {
        const isInvited = fullyInvitedCampaignIds.has(campaign.id);
        return {
          id: campaign.id,
          label: campaign.name,
          value: {
            isInvited,
          },
          disabled: isInvited,
        };
      }),
    [campaignsAcceptingProposals, fullyInvitedCampaignIds]
  );

  const selectedCampaign = useMemo(() => {
    return (
      mappedOptions.find((option) => option.id === selectedCampaignId) || null
    );
  }, [mappedOptions, selectedCampaignId]);

  const [selectedOption, setSelectedOption] = useState<Option | null>(
    selectedCampaign
  );

  useEffect(() => {
    if (isLoading) return;
    // auto select the only option if there is only one
    if (mappedOptions.length === 1 && !mappedOptions[0].disabled) {
      setSelectedOption(mappedOptions[0]);
    }
    if (mappedOptions.length === 0) {
      setIsNoActiveCampaignsModalOpen(true);
      setHasActiveCampaigns(false);
    } else {
      setHasActiveCampaigns(true);
    }
  }, [mappedOptions, setHasActiveCampaigns, isLoading]);

  const isInvitedToAllCampaigns =
    mappedOptions.length > 0 &&
    mappedOptions.every((option) => option.disabled);
  const isSelectedOptionDisabled = !!mappedOptions.find(
    (option) => option.id === selectedOption?.id
  )?.disabled;
  const personalNoteHasLink =
    showPersonalNote && PERSONAL_NOTE_LINK_PATTERN.test(personalNote);

  const handleChange = (_: React.SyntheticEvent, value: Option) => {
    setSelectedOption(value);
  };

  if (isNoActiveCampaignsModalOpen) {
    return null;
  }

  return (
    <Stack>
      <ContentContainer>
        <AutocompleteInput
          placeholder={translate(`${TRANSLATION_PREFIX}.dropdown-placeholder`)}
          options={mappedOptions}
          selectedOptions={selectedOption}
          onChange={handleChange}
          disableCloseOnSelect
          renderOption={renderOption}
        />
        <Box>
          {isInvitedToAllCampaigns ? (
            <Body size="sm" className={styles.personalNoteHint}>
              {translate(
                'Already invited to all campaigns accepting proposals'
              )}
            </Body>
          ) : null}
        </Box>
        <Box>
          {showPersonalNote ? (
            <Box className={styles.personalNoteContainer}>
              <TextField
                multiline
                rows={3}
                fullWidth
                variant="filled"
                label={translate('Personal note (optional)')}
                value={personalNote}
                onChange={(event) =>
                  setPersonalNote(
                    event.target.value.slice(0, PERSONAL_NOTE_MAX_LENGTH)
                  )
                }
                error={personalNoteHasLink}
                helperText={
                  personalNoteHasLink
                    ? translate("Links aren't allowed in the personal note")
                    : undefined
                }
                inputProps={{
                  'maxLength': PERSONAL_NOTE_MAX_LENGTH,
                  'data-testid': 'personal-note-input',
                }}
              />
              <Body size="sm" className={styles.personalNoteHint}>
                {isAllFilterMatchingSelected ||
                offPlatformCount === creators.length
                  ? translate(
                      "Included in the email to creators who aren't on Popular Pays yet"
                    )
                  : translate(
                      "Included in the email to creators who aren't on Popular Pays yet ({count} of {total} selected)",
                      {count: offPlatformCount, total: creators.length}
                    )}
                {` · ${personalNote.length}/${PERSONAL_NOTE_MAX_LENGTH}`}
              </Body>
            </Box>
          ) : null}
        </Box>
        <Box className={styles.warningContainer}>
          <Body
            className={styles.warningText}
            size="lg"
            dangerouslySetInnerHTML={{
              __html: translate(`${TRANSLATION_PREFIX}.invite-action-warning`),
            }}
          />
        </Box>
      </ContentContainer>
      <SubmitButtonContainer>
        <Button
          appearance="neutral"
          mode="filled"
          size="large"
          disabled={
            !selectedOption ||
            isSelectedOptionDisabled ||
            personalNoteHasLink ||
            !agency?.id ||
            isSubmitting
          }
          onClick={handleSubmit}
          isLoading={isSubmitting}
        >
          {translate(`${TRANSLATION_PREFIX}.submit-button`)}
        </Button>
      </SubmitButtonContainer>
    </Stack>
  );
}

export default AddToCampaign;
